import { BaseError } from '@canvas/error';

import { CanvasRendererError } from './renderer-error.ts';
import { type ErrorType, CanvasErrorType, type IdentifiedError } from './types.ts';

export function identifyErrorType(error: unknown): ErrorType {
  if (!(error instanceof Error)) {
    return {
      type: CanvasErrorType.UNKNOWN_ERROR,
      isRecoverable: false,
    };
  }

  if (
    error.name === CanvasErrorType.ANONYMOUS_NOT_SUPPORTED_ERROR ||
    error.name === CanvasErrorType.TENANT_ID_MISSING_ERROR ||
    error.name === CanvasErrorType.IDENTITY_USER_FAILED_ERROR ||
    error.name === CanvasErrorType.DYNAMIC_IMPORT_ERROR ||
    error.name === CanvasErrorType.SHADER_COMPILATION_ERROR ||
    error.name === CanvasErrorType.NO_SESSION_DATA_ERROR ||
    error.name === CanvasErrorType.RUNTIME_ASSERTION_ERROR
  ) {
    return {
      type: error.name,
      isRecoverable: false,
    };
  }

  if (
    error instanceof ErrorEvent &&
    error.error.name === CanvasErrorType.DYNAMIC_IMPORT_ERROR
  ) {
    return {
      type: CanvasErrorType.DYNAMIC_IMPORT_ERROR,
      isRecoverable: false,
    };
  }

  if (
    error instanceof CanvasRendererError &&
    (error.originalError?.name === CanvasErrorType.UNRECOVERABLE_WEB_GL_ERROR ||
      error.originalError?.name === CanvasErrorType.RECOVERABLE_WEB_GL_ERROR)
  ) {
    return {
      type: error.originalError.name,
      isRecoverable: false,
    };
  }

  return {
    type: CanvasErrorType.UNKNOWN_ERROR,
    isRecoverable: false,
  };
}

export function identifyError(error: Error): IdentifiedError {
  const { type, isRecoverable } = identifyErrorType(error);

  const ignoreUfoReporting = shouldIgnoreUfoReporting(error);

  return {
    error,
    type,
    isRecoverable,
    ignoreUfoReporting,
  };
}

export function shouldIgnoreUfoReporting(error: Error): boolean {
  if (isWebAssemblyError(error.message) || isClientError(error.message)) {
    return true;
  } else if (error instanceof BaseError) {
    return error.ignoreUfoReporting;
  }
  return false;
}

export function isWebAssemblyError(message: string): boolean {
  return message.includes('WebAssembly');
}

export function isClientError(message: string): boolean {
  return (
    // client-side (GPU driver issues, browser tab suspension, etc.
    message.includes('WebGL context lost') ||
    // there are sometimes Out of memory and out of memory errors
    message.toLowerCase().includes('out of memory') ||
    // WebGL error on client side (memory, drivers, etc.)
    message.includes('Unable to create program') ||
    // WebGL error insufficient memory
    message.includes('Array buffer allocation failed')
  );
}
